import { ChangeEvent, useEffect, useState } from 'react';
import { DateTime } from 'luxon';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTimePicker } from "@mui/x-date-pickers";
import { Grid, IconButton, InputAdornment, TextField, styled, FormControl, InputLabel, Select, SelectChangeEvent, MenuItem } from '@mui/material';
import { EntityState } from '../../gql-types.generated';
import { BlankMenuItem } from '../../util/SharedStyles';
import CloseIcon from '@mui/icons-material/Close';
import { Viewer } from "../../util/Constants";
import FilterBar from "./FilterBar";
import { capitalizeWithUnderscoreRemoval } from '../../util/Common';

const DEFAULT_DATE_FROM = DateTime.now().minus({ days: 7 }).startOf('day');
const DEFAULT_DATE_TO = DateTime.now().endOf('day');

const DatePickerWrapper = styled('div')((props) => ({
    width: '100%',
}));

interface AuditRecordsFilterBarProps {
    id?: string;
    loading: boolean;
    visible: boolean;
    viewer: Viewer | undefined;
    onClose: () => void;
    onFilterChanged: (
        filterEntityName: string | undefined,
        filterEntityState: EntityState | undefined,
        filterDateFrom?: DateTime | undefined,
        filterDateTo?: DateTime | undefined,
    ) => void;
}

const AuditRecordsFilterBar: React.FC<AuditRecordsFilterBarProps> = props => {
    const { id, loading = false, visible = false, onFilterChanged } = props;
    const [filterEntityName, setFilterEntityName] = useState<string | undefined>();
    const [filterEntityState, setFilterEntityState] = useState<EntityState | undefined>();
    const [filterDateFrom, setFilterDateFrom] = useState<DateTime | undefined>(DEFAULT_DATE_FROM);
    const [filterDateTo, setFilterDateTo] = useState<DateTime | undefined>(DEFAULT_DATE_TO);
    const fieldsDisabled = visible && loading;

    // Trigger initial search when component mounts with default dates
    useEffect(() => {
        onFilterChanged(undefined, undefined, DEFAULT_DATE_FROM, DEFAULT_DATE_TO);
    }, []); // Empty dependency array means this runs once on mount

    const onSearchClick = () => {
        onFilterChanged(filterEntityName, filterEntityState, filterDateFrom, filterDateTo);
    }

    const onCloseClick = () => {
        clearFilters();
        props.onClose();
        onFilterChanged(undefined, undefined, DEFAULT_DATE_FROM, DEFAULT_DATE_TO);
    };    

    const clearFilters = () => {
        clearEntityNameFilter();
        clearEntityStateFilter();
        setFilterDateFrom(DEFAULT_DATE_FROM);
        setFilterDateTo(DEFAULT_DATE_TO);
    };

    const clearEntityNameFilter = () => {
        setFilterEntityName(undefined);
    };

    const clearEntityStateFilter = () => {
        setFilterEntityState(undefined);
    };

    const onEntityNameFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        setFilterEntityName(value);
    };

    const onEntityStateFilterChange = (event: SelectChangeEvent<string>) => {
        const {
            target: { value },
        } = event;
        setFilterEntityState(value.length > 0 ? value as EntityState : undefined);
    };

    const getEntityStateFilterList = () => {
        let items = [];
        const blankItem = <BlankMenuItem key="blank" value="" />;
        items.push(blankItem);
        
        Object.values(EntityState).forEach((state) => {
            items.push(
                <MenuItem key={state} value={state}>
                    {capitalizeWithUnderscoreRemoval(state, " ")}
                </MenuItem>
            );
        });
        
        return items;
    };

    const onDateFromFilterChange = (value: DateTime | null) => {
        let newDateFrom = value ?? undefined;
        setFilterDateFrom(newDateFrom);
        setFilterDateTo(newDateFrom?.plus({ days: 7 }).endOf('day') ?? undefined);
    };
    
    const onDateToFilterChange = (value: DateTime | null) => {
        setFilterDateTo(value ?? undefined);
    };

    const entityNameFilterProps = {
        endAdornment: (
            <InputAdornment position="end">
                <IconButton
                    aria-label="clear entity name filter"
                    onClick={clearEntityNameFilter}
                    disabled={fieldsDisabled}
                >
                    {filterEntityName && filterEntityName.length > 0 ? <CloseIcon fontSize='small' sx={{ padding: '2px' }} /> : null}
                </IconButton>
            </InputAdornment>
        )
    };

    return (
        <FilterBar id={id} visible={visible} onClose={onCloseClick} onSearch={onSearchClick}>
            <Grid container item direction={'row'} ml={{xs:1, sm:3, md:6, lg:10, xl:14}} columnGap={2} xs={12} justifyContent="flex-start">
            <Grid item xs={2}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePickerWrapper>
                            <DateTimePicker
                                label="Date From"
                                value={filterDateFrom ?? null}
                                views={['year','month','day','hours','minutes']}
                                openTo={'day'}
                                disabled={fieldsDisabled}
                                onChange={onDateFromFilterChange}
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        fullWidth: true,
                                        inputProps: {'aria-label': 'date from'}
                                    },
                                    field: { clearable: true }
                                }}
                                data-cy="audit-records-filter-date-from"
                            />
                        </DatePickerWrapper>
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={2}>
                    <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <DatePickerWrapper>
                            <DateTimePicker
                                label="Date To"
                                value={filterDateTo ?? null}
                                views={['year','month','day','hours','minutes']}
                                openTo={'day'}
                                minDateTime={filterDateFrom}
                                disabled={fieldsDisabled}
                                onChange={onDateToFilterChange}
                                slotProps={{
                                    textField: {
                                        variant: "standard",
                                        fullWidth: true,
                                        inputProps: {'aria-label': 'date to'}
                                    },
                                    field: { clearable: true }
                                }}
                                data-cy="audit-records-filter-date-to"
                            />
                        </DatePickerWrapper>
                    </LocalizationProvider>
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        itemID="audit-records-filter-entity-name"
                        fullWidth
                        value={filterEntityName ?? ''}
                        label="Entity Name"
                        disabled={fieldsDisabled}
                        inputProps={{ 'aria-label': 'entity name', 'maxLength': 50 }}
                        InputProps={entityNameFilterProps}
                        onChange={onEntityNameFilterChange}
                        autoComplete="off"
                        data-cy="audit-records-filter-entity-name"
                        variant="standard"
                    />
                </Grid>
                <Grid item xs={2}>
                    <FormControl variant="standard" fullWidth disabled={fieldsDisabled}>
                        <InputLabel id="audit-records-filter-entity-state-label">Entity State</InputLabel>
                        <Select
                            labelId="audit-records-filter-entity-state-label"
                            id="audit-records-filter-entity-state"
                            value={filterEntityState ?? ''}
                            onChange={onEntityStateFilterChange}
                            inputProps={{ 'aria-label': 'entity state' }}
                            data-cy="audit-records-filter-entity-state"
                        >
                            {getEntityStateFilterList()}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
        </FilterBar>
    );
};

export default AuditRecordsFilterBar;
